<template>
  <div class="quill-form-item">
    <div class="editor-container">
      <div class="custom-toolbar">
        <a href="javascript:void(0)" class="html-button" @click.prevent="showHtmlCode" title="查看/编辑HTML源码">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor"
              d="M12 17.56l4.07-1.13.55-6.1H9.38L9.2 8.3h7.6l.2-1.99H7l.56 6.01h6.89l-.23 2.58-2.22.6-2.22-.6-.14-1.66h-2l.29 3.19L12 17.56M4.07 3h15.86L18.5 19.2 12 21l-6.5-1.8L4.07 3z" />
          </svg>
        </a>
        <a href="javascript:void(0)" class="audio-button" @click.prevent="customAudioHandler" title="上传音频">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor"
              d="M12,3V15A4,4 0 0,1 8,19A4,4 0 0,1 4,15A4,4 0 0,1 8,11V5A4,4 0 0,1 12,1A4,4 0 0,1 16,5V11A4,4 0 0,1 20,15A4,4 0 0,1 16,19A4,4 0 0,1 12,15V3M8,13A2,2 0 0,0 6,15A2,2 0 0,0 8,17A2,2 0 0,0 10,15A2,2 0 0,0 8,13M16,13A2,2 0 0,0 14,15A2,2 0 0,0 16,17A2,2 0 0,0 18,15A2,2 0 0,0 16,13Z" />
          </svg>
        </a>
      </div>
      <QuillEditor ref="quillEditor" v-model:content="internalContent" :content-type="contentType"
        :options="editorOptions" />
    </div>
  </div>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';

// 导入Quill并注册字号样式
import { Quill } from '@vueup/vue-quill';

// 定义 Audio Blot
const AudioBlot = Quill.import('blots/block/embed');

class CustomAudioBlot extends AudioBlot {
  static create(value) {
    const node = super.create();
    node.setAttribute('controls', 'controls');
    node.setAttribute('src', value);
    return node;
  }

  static value(node) {
    return node.getAttribute('src');
  }
}

CustomAudioBlot.blotName = 'audio';
CustomAudioBlot.tagName = 'audio';

// 注册自定义 Audio Blot
Quill.register(CustomAudioBlot);

// 定义字号白名单
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px'];
Quill.register(SizeStyle, true);

export default {
  name: 'RichTextEditor',
  components: { QuillEditor },
  emits: ['update:content', 'image-uploaded', 'video-uploaded', 'audio-uploaded', 'error'],

  props: {
    // 编辑器内容
    content: {
      type: String,
      default: ''
    },
    // 内容类型 (html或text)
    contentType: {
      type: String,
      default: 'html'
    },
    // 上传图片大小限制（MB）
    maxImageSize: {
      type: Number,
      default: 2
    },
    // 上传视频大小限制（MB）
    maxVideoSize: {
      type: Number,
      default: 10
    },
    // 上传音频大小限制（MB）
    maxAudioSize: {
      type: Number,
      default: 2
    },
    // 可接受的图片文件类型
    acceptImageTypes: {
      type: Array,
      default: () => ['image/jpeg', 'image/png', 'image/gif', 'image/bmp']
    },
    // 可接受的视频文件类型
    acceptVideoTypes: {
      type: Array,
      default: () => ['video/mp4']
    },
    // 可接受的音频文件类型（仅允许mp3/wma/wav/amr）
    acceptAudioTypes: {
      type: Array,
      default: () => ['audio/mp3', 'audio/mpeg', 'audio/wma', 'audio/x-ms-wma', 'audio/wav', 'audio/x-wav', 'audio/amr', 'audio/amr-wb']
    },
    // 是否显示HTML源码按钮
    showHtmlButton: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      // 编辑器内部内容副本
      internalContent: this.content,
      // 编辑器配置选项
      editorOptions: {
        modules: {
          toolbar: {
            container: [
              // 第一行工具
              ['bold', 'italic', 'underline', 'strike'],        // 加粗、斜体、下划线、删除线
              ['blockquote', 'code-block'],                     // 引用、代码块
              [{ 'header': 1 }, { 'header': 2 }],               // 标题1、标题2
              [{ 'list': 'ordered' }, { 'list': 'bullet' }],     // 有序列表、无序列表
              [{ 'script': 'sub' }, { 'script': 'super' }],      // 下标、上标
              [{ 'indent': '-1' }, { 'indent': '+1' }],          // 减少缩进、增加缩进
              [{ 'direction': 'rtl' }],                         // 文本方向

              // 第二行工具
              [{ 'size': ['12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px'] }], // 字号
              [{ 'header': [1, 2, 3, 4, 5, 6, false] }],        // 标题级别
              [{ 'color': [] }, { 'background': [] }],          // 文字颜色、背景色
              [{ 'font': [] }],                                 // 字体
              [{ 'align': [] }],                                 // 对齐方式

              // 第三行工具
              ['link', 'image', 'video'],              // 链接、图片、视频、音频
              ['clean']                                        // 清除格式
            ],
            handlers: {
              image: this.customImageHandler,
              video: this.customVideoHandler,
              audio: this.customAudioHandler
            }
          }
        },
        placeholder: '请输入内容...',
        theme: 'snow'
      }
    }
  },

  watch: {
    // 观察外部content变化并更新内部状态
    content(newVal) {
      if (newVal !== this.internalContent) {
        this.internalContent = newVal;
      }
    },
    // 观察内部内容变化并触发更新事件
    internalContent(newVal) {
      this.$emit('update:content', newVal);
    }
  },

  methods: {
    // 获取Quill实例
    getQuillInstance() {
      return this.$refs.quillEditor?.getQuill();
    },

    // 获取HTML源码
    getHtmlContent() {
      return this.internalContent;
    },

    // 显示HTML代码弹窗
    showHtmlCode() {
      this.$prompt('HTML代码', '编辑HTML源码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputValue: this.internalContent,
        customClass: 'html-code-dialog',
        inputAttributes: {
          rows: 20,
          style: 'width: 100%; font-family: monospace; white-space: pre;'
        },
        beforeClose: (action, instance, done) => {
          if (action === 'confirm') {
            if (!instance.inputValue) {
              this.$message.error('HTML内容不能为空');
              return;
            }
            done();
          } else {
            done();
          }
        }
      }).then(({ value }) => {
        this.internalContent = value;
        const quill = this.getQuillInstance();
        if (quill) {
          quill.setContents(quill.clipboard.convert(value));
        }
        this.$message.success('HTML代码已更新');
      }).catch(() => {
        this.$message.info('已取消编辑');
      });
    },

    // 自定义图片处理器
    async customImageHandler() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', this.acceptImageTypes.join(','));
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        const quill = this.getQuillInstance();
        if (!quill) return;

        let range = null;
        try {
          range = quill.getSelection();
          if (!range) {
            this.$emit('error', '请先在编辑器中选择插入位置');
            return;
          }

          // 验证文件
          if (!this.validateImageFile(file)) return;

          // 插入上传中占位符
          quill.insertEmbed(range.index, 'image', '上传中...');

          // 上传图片
          const imageUrl = await this.uploadFile(file, 'image');

          // 替换为真实图片
          quill.deleteText(range.index, 1);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);

          this.$emit('image-uploaded', { url: imageUrl, size: file.size, type: file.type });
        } catch (error) {
          console.error('图片处理失败:', error);
          this.handleUploadError(error, quill, range, '图片');
        }
      };
    },

    // 自定义视频处理器
    async customVideoHandler() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', this.acceptVideoTypes.join(','));
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        const quill = this.getQuillInstance();
        if (!quill) return;

        let range = null;
        try {
          range = quill.getSelection();
          if (!range) {
            this.$emit('error', '请先在编辑器中选择插入位置');
            return;
          }

          // 验证文件
          if (!this.validateVideoFile(file)) return;

          // 插入上传中占位符
          quill.insertText(range.index, '[视频上传中...]');

          // 上传视频
          const videoUrl = await this.uploadFile(file, 'video');

          // 替换为真实视频
          quill.deleteText(range.index, 10);
          quill.insertEmbed(range.index, 'video', videoUrl);
          quill.setSelection(range.index + 1);

          this.$emit('video-uploaded', { url: videoUrl, size: file.size, type: file.type });
        } catch (error) {
          console.error('视频处理失败:', error);
          this.handleUploadError(error, quill, range, '视频');
        }
      };
    },

    // 自定义音频处理器
    async customAudioHandler() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', this.acceptAudioTypes.join(','));
      input.click();

      input.onchange = async () => {
        const file = input.files[0];
        const quill = this.getQuillInstance();
        if (!quill) return;

        let range = null;
        try {
          range = quill.getSelection();
          if (!range) {
            this.$emit('error', '请先在编辑器中选择插入位置');
            return;
          }

          // 验证文件
          if (!this.validateAudioFile(file)) return;

          // 插入上传中占位符
          quill.insertText(range.index, '[音频上传中');

          // 上传音频
          const audioUrl = await this.uploadFile(file, 'audio');

          // 替换为真实音频
          quill.deleteText(range.index, 7);
          quill.insertEmbed(range.index, 'audio', audioUrl);
          quill.setSelection(range.index + 1);

          this.$emit('audio-uploaded', { url: audioUrl, size: file.size, type: file.type });
        } catch (error) {
          console.error('音频处理失败:', error);
          this.handleUploadError(error, quill, range, '音频');
        }
      };
    },

    // 图片验证
    validateImageFile(file) {
      if (!this.acceptImageTypes.includes(file.type)) {
        this.$emit('error', `只能上传 ${this.acceptImageTypes.join('/')} 格式图片`);
        return false;
      }

      if (file.size / 1024 / 1024 > this.maxImageSize) {
        this.$emit('error', `图片大小不能超过 ${this.maxImageSize}MB`);
        return false;
      }

      return true;
    },

    // 视频验证
    validateVideoFile(file) {
      if (!this.acceptVideoTypes.includes(file.type)) {
        this.$emit('error', `只能上传 ${this.acceptVideoTypes.join('/')} 格式视频`);
        return false;
      }

      if (file.size / 1024 / 1024 > this.maxVideoSize) {
        this.$emit('error', `视频大小不能超过 ${this.maxVideoSize}MB`);
        return false;
      }

      return true;
    },

    // 音频验证
    validateAudioFile(file) {
      // 检查文件扩展名是否在允许的列表中
      const allowedExtensions = ['mp3', 'wma', 'wav', 'amr'];
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        this.$emit('error', '只能上传 mp3/wma/wav/amr 格式的音频文件');
        return false;
      }

      if (file.size / 1024 / 1024 > this.maxAudioSize) {
        this.$emit('error', `音频大小不能超过 ${this.maxAudioSize}MB`);
        return false;
      }

      return true;
    },

    // 处理上传错误
    handleUploadError(error, quill, currentRange, type = '文件') {
      const message = error.message || `${type}上传失败`;
      this.$emit('error', message);

      if (quill && currentRange) {
        const length = type === '图片' ? 1 : (type === '视频' ? 10 : 7);
        quill.deleteText(currentRange.index, length);
      }
    },

    // 使用OSS上传文件
    async uploadFile(file, type = 'image') {
      try {
        const signature = await getUploadSignature(type);
        const res = await uploadToOSS(file, signature);
        return res.url;
      } catch (error) {
        console.error(`${type}上传失败:`, error);
        throw new Error(`${type}上传失败: ` + (error.message || error));
      }
    },

    // 清空编辑器内容
    clearContent() {
      this.internalContent = '';

      // 获取Quill实例并清除内容
      const quill = this.getQuillInstance();
      if (quill) {
        quill.setText('');
      }
    },

    // 设置编辑器内容
    setContent(content) {
      this.internalContent = content;

      // 获取Quill实例并设置内容
      const quill = this.getQuillInstance();
      if (quill && content) {
        quill.setContents(quill.clipboard.convert(content));
      }
    }
  }
}
</script>

<style scoped>
.editor-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  width: 100%;
  max-width: 800px;
  position: relative;
}

:deep(.ql-editor) {
  min-height: 200px;
  font-size: 14px;
  line-height: 1.5;
}

:deep(.ql-container) {
  height: 400px !important;
  border-radius: 0 0 4px 4px !important;
  border-color: #dcdfe6 !important;
  border-top: none !important;
}

:deep(.ql-toolbar) {
  border-radius: 4px 4px 0 0 !important;
  border-color: #dcdfe6 !important;
  background-color: #f5f7fa !important;
  padding: 8px 12px !important;
}

:deep(.ql-toolbar button) {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.ql-toolbar button:hover) {
  background-color: #e4e7ed;
}

:deep(.ql-toolbar button svg) {
  width: 16px;
  height: 16px;
}

/* 自定义HTML按钮样式 */
.custom-toolbar {
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 10;
}

.html-button {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  color: #444;
  background-color: #fff;
  margin-left: 18px;
}

.html-button:hover {
  background-color: #e4e7ed;
}

/* 音频按钮样式 */
.audio-button {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  color: #444;
  background-color: #fff;
  margin-left: 18px;
}

.audio-button:hover {
  background-color: #e4e7ed;
}

/* 视频样式 */
:deep(.ql-editor iframe) {
  width: 100%;
  height: 400px;
  border: none;
}

:deep(.ql-video) {
  width: 100%;
  height: 400px;
}

/* 音频样式 */
:deep(.ql-editor audio) {
  width: 100%;
  max-width: 500px;
  margin: 10px 0;
}
</style>

<style>
/* 字号选择器的CSS样式（全局样式） */
.ql-picker-item[data-value='12px']::before,
.ql-picker-label[data-value='12px']::before {
  content: '12px' !important;
}

.ql-picker-item[data-value='14px']::before,
.ql-picker-label[data-value='14px']::before {
  content: '14px' !important;
}

.ql-picker-item[data-value='16px']::before,
.ql-picker-label[data-value='16px']::before {
  content: '16px' !important;
}

.ql-picker-item[data-value='18px']::before,
.ql-picker-label[data-value='18px']::before {
  content: '18px' !important;
}

.ql-picker-item[data-value='20px']::before,
.ql-picker-label[data-value='20px']::before {
  content: '20px' !important;
}

.ql-picker-item[data-value='24px']::before,
.ql-picker-label[data-value='24px']::before {
  content: '24px' !important;
}

.ql-picker-item[data-value='30px']::before,
.ql-picker-label[data-value='30px']::before {
  content: '30px' !important;
}

.ql-picker-item[data-value='32px']::before,
.ql-picker-label[data-value='32px']::before {
  content: '32px' !important;
}

.ql-picker-item[data-value='36px']::before,
.ql-picker-label[data-value='36px']::before {
  content: '36px' !important;
}

/* HTML代码对话框样式 */
.html-code-dialog .el-textarea__inner {
  font-family: 'Courier New', Courier, monospace !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  white-space: pre !important;
}
</style>